import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useCurrentApp } from '@/stores/userDataStore';
import { getPlayAppMedia, PlayAppMediaAssetResponse } from '../../../api/play-app-media';
import { ImageProviders, MediaAsset, SourceMediaAsset, PlayAppMediaAssetStatus, MediaAssetType } from '@/types/media';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { reactQueryClient } from '@/api/react-query/query-client';
const PAGE_SIZE = 30;
const TWO_MINUTES_IN_MILLIS = 120000;

export type PlayAppMediaAsset = SourceMediaAsset & {
  id: string;
  source?: string;
  status?: PlayAppMediaAssetStatus;
  name?: string;
  createdAt?: string;
};

export const providerToSourceMap = {
  [MediaAssetProviders.playApp]: undefined,
  [MediaAssetProviders.instagram]: MediaAssetProviders.instagram,
  [MediaAssetProviders.tiktok]: MediaAssetProviders.tiktok,
};

export type VideoServiceMediaInfiniteQueryProps = {
  query: string;
  provider: MediaAssetProviders;
  type?: MediaAssetType.image | MediaAssetType.video;
};

export const useVideoServiceMediaInfiniteQuery = (props: VideoServiceMediaInfiniteQueryProps) => {
  const { query, provider, type = MediaAssetType.video } = props;

  const { appKey, appUrl, isLoggedIn } = useCurrentApp();
  const source = providerToSourceMap[provider];
  const queryVariables = {
    appKey: appKey,
    appUrl: appUrl,
    source,
    textQuery: query,
  };

  const { data, ...queryOptions } = useInfiniteQuery<PlayAppMediaAssetResponse>({
    queryKey: ['video-service-media', type, queryVariables],
    queryFn: async ({ pageParam }) => {
      const response = await getPlayAppMedia({
        appKey,
        from: pageParam as number,
        size: PAGE_SIZE,
        type: type || MediaAssetType.video,
        source,
        textQuery: query,
      });

      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextFrom = lastPage.from + lastPage.itemsCount;
      const hasNext = nextFrom < lastPage.total;
      return hasNext ? nextFrom : null;
    },
    staleTime: TWO_MINUTES_IN_MILLIS,
    enabled: !!appKey && !!appUrl && isLoggedIn,
  });

  const assets: MediaAsset[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const playMediaAssets: PlayAppMediaAsset[] = data.pages.flatMap((page) => page.items);
    return playMediaAssets.map(
      (asset) =>
        ({
          ...asset,
          alt: asset.id,
          provider: ImageProviders.library,
        }) satisfies MediaAsset
    );
  }, [data]);

  const invalidateQuery = () => {
    reactQueryClient.invalidateQueries({
      queryKey: ['video-service-media', type, queryVariables],
    });
  };

  return {
    assets,
    invalidateQuery,
    ...queryOptions,
  };
};
